import { useToast } from '@chakra-ui/react';
import { setIn } from 'formik';
import React, { useCallback } from 'react';

import { CreditAgreementFullArtifactWipConfigType } from '../../types';
import {
  errorToastConfig,
  errorToastId,
  successToastConfig,
} from 'packages/common-frontend/constants';
import { GET_CREDIT_AGREEMENT_FULL_ARTIFACT_WIP } from 'packages/creditAgreements/requests/requests.gql';
import {
  UpdateLenderCreditAgreementFullArtifactWipMutationFnGQLType,
  useUpdateLenderCreditAgreementFullArtifactWipMutation,
} from 'packages/creditAgreements/requests/requests.gql.generated';

interface SaveCreditAgreementWipInputs {
  creditFacilityId?: string;
}
export function useSaveLenderWipCreditAgreement({
  creditFacilityId = '',
}: SaveCreditAgreementWipInputs): {
  saveCreditAgreementFullArtifactWip: UpdateLenderCreditAgreementFullArtifactWipMutationFnGQLType;
  isSaveCreditAgreementFullArtifactWipLoading: boolean;
  saveAndUpdateConfigCreditAgreementWip: (inputs: {
    wipConfig: CreditAgreementFullArtifactWipConfigType;
    propertyKey: string;
    updatedProperty: any;
    setIsSaveLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  }) => Promise<void>;
} {
  const toast = useToast();

  const [
    saveCreditAgreementFullArtifactWip,
    { loading: isSaveCreditAgreementFullArtifactWipLoading },
  ] = useUpdateLenderCreditAgreementFullArtifactWipMutation({
    onCompleted: () => {
      if (!toast.isActive(successToastConfig.id)) {
        successToastConfig.title =
          'Successfully saved credit agreement document wip.';
        toast(successToastConfig);
      }
    },
    onError: () => {
      if (!toast.isActive(errorToastId)) {
        errorToastConfig.title = `Error saving credit agreement.`;
        toast(errorToastConfig);
      }
    },
    refetchQueries: [
      {
        query: GET_CREDIT_AGREEMENT_FULL_ARTIFACT_WIP,
        variables: { creditFacilityId },
      },
    ],
  });

  const saveAndUpdateConfigCreditAgreementWip = useCallback(
    async (inputs: {
      wipConfig: CreditAgreementFullArtifactWipConfigType;
      propertyKey: string;
      updatedProperty: any;
      setIsSaveLoading?: React.Dispatch<React.SetStateAction<boolean>>;
    }): Promise<void> => {
      const {
        wipConfig,
        propertyKey,
        updatedProperty,
        setIsSaveLoading,
      } = inputs;

      if (setIsSaveLoading) {
        setIsSaveLoading(true);
      }

      // Save new CA Wip into DB, which triggers refetch with updated config
      const updatedWipCA = setIn(
        wipConfig.formProps.values,
        propertyKey,
        updatedProperty,
      );

      const saveData = await saveCreditAgreementFullArtifactWip({
        variables: {
          creditAgreementFullArtifactWip: {
            id: updatedWipCA.id,
            creditFacilityId: updatedWipCA.creditFacilityId,
            creditAgreement: JSON.stringify(updatedWipCA),
          },
        },
      });

      if (saveData && !saveData.errors) {
        // Update formik props itself too since it doesn't reset on refetch
        // Only update formik props if the save was successful
        wipConfig.formProps.setFieldValue(propertyKey, updatedProperty);
      }

      if (setIsSaveLoading) {
        setIsSaveLoading(false);
      }
    },
    [saveCreditAgreementFullArtifactWip],
  );

  return {
    saveCreditAgreementFullArtifactWip,
    isSaveCreditAgreementFullArtifactWipLoading,
    saveAndUpdateConfigCreditAgreementWip,
  };
}
